import { createStyles, makeStyles, Typography, IconButton, Grid } from "@material-ui/core";
import React, { useState } from "react";
import { ExamTemplateWithStructureIndex, ExamType, ExamTypeTranslationKey } from "../../model/Exam";
import { ColumnDefinition, CustomGrid } from "../common/customGrid/CustomGrid";
import TranslatedMessage from "../translations/TranslatedMessage";
import { getExamsIncludedInPublishedCourses } from "../../api/ExamApi";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import PublishedExamSelectionDialog from "./PublishedExamSelectionDialog";
import { GetTranslation } from "../../translations/GetTranslation";
import { useIntl } from "react-intl";
import { GetTranslatedValue } from "../../model/Shared/TranslatedValue";
import { CourseType, CourseTypeTranslationKey } from "../../model/Course";
import EditIcon from "@material-ui/icons/Edit";
import EditExamSessionExamCourseTypesDialog from "./EditExamSessionExamCourseTypesDialog";

const useStyles = makeStyles(() =>
  createStyles({
    header: {
      marginBottom: "-3rem",
    },
  })
);

type ExamSessionFormExamsProps = {
  exams: ExamTemplateWithStructureIndex[];
  handleAddExams: (exams: ExamTemplateWithStructureIndex[]) => void;
  handleRemoveExam: (exam: ExamTemplateWithStructureIndex) => void;
  handleEditExam: (exam: ExamTemplateWithStructureIndex, courseTypes: CourseType[]) => void;
  isReadOnly: boolean;
};

const ExamSessionFormExams = (props: ExamSessionFormExamsProps) => {
  const { exams, handleAddExams: addExamItems, handleEditExam, handleRemoveExam, isReadOnly } = props;
  const [allExams, setAllExams] = React.useState<ExamTemplateWithStructureIndex[]>([]);
  const [selectedExam, setSelectedExam] = useState<ExamTemplateWithStructureIndex | null>(null);
  const intl = useIntl();

  React.useEffect(() => {
    const timer = setTimeout(() => {
      getExamsIncludedInPublishedCourses().then((exams) => {
        setAllExams(exams);
      });
    }, 250);
    return () => clearTimeout(timer);
  }, []);

  const [selectionDialogOpened, setSelectionDialogOpened] = React.useState(false);
  const classes = useStyles();

  const columnsDefinitions: ColumnDefinition<ExamTemplateWithStructureIndex>[] = [
    {
      columnKey: "courseTypes",
      columnLabel: <TranslatedMessage id="exam.field.courseType" />,
      cellValue: (_) =>
        _.courseTypes.map((courseType) => GetTranslation(intl, CourseTypeTranslationKey[courseType])).join(", "),
    },
    {
      columnKey: "examName",
      columnLabel: <TranslatedMessage id="exam.field.examName" />,
      cellValue: (_) => GetTranslatedValue(_.name),
    },
    {
      columnKey: "courses",
      columnLabel: <TranslatedMessage id="course.list.header" />,
      cellValue: (_) => GetTranslatedValue(_.includedInCourses),
    },
    {
      columnKey: "modules",
      columnLabel: <TranslatedMessage id="module.header" />,
      cellValue: (_) => GetTranslatedValue(_.includedInModules),
    },
    {
      columnKey: "examType",
      columnLabel: <TranslatedMessage id="exam.field.type" />,
      cellValue: (_) => GetTranslation(intl, ExamTypeTranslationKey[_.type]),
    },
  ];

  const onNewExamSelection = (exams: ExamTemplateWithStructureIndex[]) => {
    var newExams = exams.map((exam) => (exam = { ...exam, courseTypes: exam.availableCourseTypes }));
    setSelectionDialogOpened(false);
    addExamItems(newExams);
  };

  const getSelectableExams = () => allExams.filter((x) => exams.every((y) => y.id !== x.id));

  if (!isReadOnly) {
    columnsDefinitions.push({
      columnKey: "removeExam",
      columnLabel: "",
      cellValue: (row) => (
        <div style={{ textAlign: "end" }}>
          {row.type === ExamType.Oral || row.type === ExamType.Written ? (
            <IconButton
              color="secondary"
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                setSelectedExam(row);
              }}
            >
              <EditIcon />
            </IconButton>
          ) : null}
          <IconButton
            color="secondary"
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              handleRemoveExam(row);
            }}
          >
            <HighlightOffIcon />
          </IconButton>
        </div>
      ),
      isSortingDisabled: true,
    });
  }

  return (
    <Grid container={true} direction="column">
      <Grid item={true}>
        <Typography variant="h6" className={isReadOnly ? "" : classes.header}>
          <TranslatedMessage id="exam.header" />
        </Typography>
      </Grid>

      <Grid item={true}>
        <CustomGrid<ExamTemplateWithStructureIndex>
          columnDefinitions={columnsDefinitions}
          rows={exams}
          rowKey={(_) => _.id}
          addButtonTooltip={<TranslatedMessage id="exam.button.new" />}
          onAddClick={() => setSelectionDialogOpened(true)}
          hideSearchBar={true}
          hideAddButton={isReadOnly}
        />
      </Grid>

      <Grid item={true}>
        <PublishedExamSelectionDialog
          handleAccept={onNewExamSelection}
          handleCancel={() => setSelectionDialogOpened(false)}
          isOpened={selectionDialogOpened}
          selectableExams={getSelectableExams()}
        />
      </Grid>

      {selectedExam && !isReadOnly && (
        <EditExamSessionExamCourseTypesDialog
          isOpened={selectedExam !== null}
          handleCancel={() => setSelectedExam(null)}
          handleAccept={(selectedCourseTypes) => {
            handleEditExam(selectedExam, selectedCourseTypes);
            setSelectedExam(null);
          }}
          selectedCourseTypes={selectedExam && selectedExam.courseTypes}
          availableCourseTypes={selectedExam.availableCourseTypes}
        />
      )}
    </Grid>
  );
};

export default ExamSessionFormExams;
